import React from "react";
// UI Framework
// Common Components
import { CheckBox, FileDownloader, Button, Modal } from "src/Components";
import TableTemplate, {
  IFilterTableCols,
} from "src/Tables/TableTemplate/tableTemplate";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
// Local Components
import SettingModal from "./SettingModal";
import ExpectedReturn from "./ExpectedReturn";
import OrderBook from "./OrderBook";
import PairPositionSearchParamUI from "./PairPositionSearchParamUI";
import FilterSettingModal from "./FilterSettingModal";
import CoversionModal from "./ConversionModal";
// Requests
import {
  getPaiPositionExcel,
  getPairPositionTable,
} from "src/Requests/synthetic";
// Types
import {
  ISelectedTableInfo,
  ISyntheticPairPositionTableReport,
  IPairPositionSearchParams,
  SyntheticApiTab,
} from "src/Interfaces/Synthetic";
import { Report } from "src/Interfaces/TableInterfaces";
// Utilities
import * as R from "ramda";

enum ModalMode {
  Closed,
  Setting,
  ExpectedReturn,
  FilterSetting,
}
interface IVisibleHeaders {
  all: boolean;
  mediator: boolean;
}
interface IProps {
  apiTab?: SyntheticApiTab;
}
const ArtificialPairPosition: React.FC<IProps> = ({ apiTab }) => {
  // refs
  const prevRowRef = React.useRef<number | null>(null);
  // local store
  const [searchParam, setSearchParam] =
    React.useState<IPairPositionSearchParams>({} as IPairPositionSearchParams);
  const [serverSearchParams, setServerSearchParams] =
    React.useState<IPairPositionSearchParams>({} as IPairPositionSearchParams);
  const [modalMode, setModalMode] = React.useState(ModalMode.Closed);
  const [conversionOpen, setConversionOpen] = React.useState(false);
  const [selectedTableInfo, setSelectedTableInfo] =
    React.useState<ISelectedTableInfo>({} as ISelectedTableInfo);
  const [visibleHeaders, setVisibleHeaders] = React.useState(
    {} as IVisibleHeaders,
  );
  const [refreshFlag, setRefreshFlag] = React.useState(false);

  const changeHandler = (name: string, value: any) => {
    setVisibleHeaders({ ...visibleHeaders, [name]: value } as IVisibleHeaders);
  };

  const searchParamChangeHandler = (name: string, value: any) => {
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
  };
  const handleExcelDownload = () => getPaiPositionExcel(searchParam, apiTab);

  const handleSearch = () => {
    const cleanObject = R.pickBy(
      (v) => !(v === null || v === "" || (Array.isArray(v) && v.length === 0)),
      searchParam,
    );
    setServerSearchParams(cleanObject as IPairPositionSearchParams);
  };

  const getFilterCols = (): IFilterTableCols => {
    const result: IFilterTableCols = {
      show: true,
      tableName: "artificialPairPositionTable",
      allowedPositions: [],
    };
    if (visibleHeaders.all) result.allowedPositions.push("DefaultHidden");
    if (visibleHeaders.mediator) result.allowedPositions.push("Mediator");
    return result;
  };

  const getModalUI = React.useMemo(() => {
    switch (modalMode) {
      case ModalMode.Setting:
        return (
          <SettingModal
            apiTab={apiTab}
            onClose={() => setModalMode(ModalMode.Closed)}
          />
        );

      case ModalMode.ExpectedReturn:
        return (
          <ExpectedReturn
            apiTab={apiTab}
            onClose={() => setModalMode(ModalMode.Closed)}
          />
        );
      case ModalMode.FilterSetting:
        return (
          <FilterSettingModal
            apiTab={apiTab}
            onClose={() => setModalMode(ModalMode.Closed)}
            onApplied={() => setRefreshFlag((prev) => !prev)}
          />
        );
      default:
        return <></>;
    }
  }, [apiTab, modalMode]);

  return (
    <>
      {conversionOpen ? (
        <Modal
          UI={<CoversionModal />}
          title="نمودار کانورژن"
          onClose={() => setConversionOpen(false)}
          fullScreen
        />
      ) : null}
      {modalMode !== ModalMode.Closed ? (
        <Modal
          UI={getModalUI}
          title={
            modalMode === ModalMode.Setting
              ? "تنظیمات"
              : modalMode === ModalMode.ExpectedReturn
                ? "بازده انتظاری"
                : "تنظیمات فیلتر"
          }
          onClose={() => setModalMode(ModalMode.Closed)}
          modalBoxClassName="!min-w-min !min-h-min"
        />
      ) : null}
      <div className="flex flex-col justify-start items-start gap-2 relative pt-5 mx-auto p-8 w-full">
        <div className="flex justify-start items-center w-full">
          <div className="flex flex-row justify-start items-center gap-5">
            <div className="bg-sky-200 rounded py-1 px-2">
              <CheckBox
                checked={visibleHeaders.all}
                label="جزئیات"
                name="all"
                onChange={changeHandler}
              />
            </div>
            <div className="bg-red-200 rounded py-1 px-2">
              <CheckBox
                checked={visibleHeaders.mediator}
                label="محاسبات"
                name="mediator"
                onChange={changeHandler}
              />
            </div>
            <FileDownloader getFile={handleExcelDownload} />
            <Button
              onClick={() => setConversionOpen(true)}
              text="نمودار کانورژن"
              variant="outlined"
            />
            <Button
              onClick={() => setModalMode(ModalMode.Setting)}
              text="تنظیمات"
              variant="outlined"
            />
          </div>
          <div className="px-4">
            <Button
              onClick={() => setModalMode(ModalMode.ExpectedReturn)}
              text="بازده انتظاری"
              variant="outlined"
            />
          </div>
          <div>
            <Button
              onClick={() => setModalMode(ModalMode.FilterSetting)}
              text="تنظیمات فیلتر"
              variant="outlined"
            />
          </div>
        </div>
        <OrderBook selectedTableInfo={selectedTableInfo} />
        <div className="flex flex-row justify-start items-start gap-1 w-full mt-7 h-fit">
          <PairPositionSearchParamUI
            apiTab={apiTab}
            searchParam={searchParam}
            searchParamChangeHandler={searchParamChangeHandler}
            onSubmit={handleSearch}
          />
          <div className="border border-gray-300 rounded flex min-w-0 flex-1 justify-center items-center gap-3 overflow-x-hidden">
            <TableTemplate<
              IPairPositionSearchParams,
              ISyntheticPairPositionTableReport
            >
              TableUI={({ reports, onAction, sortingType, setSortingType }) => (
                <CommonTableUI
                  reports={reports}
                  onAction={onAction}
                  filterCols={getFilterCols()}
                  sortingType={sortingType}
                  setSortingType={setSortingType}
                  refreshFlag={refreshFlag}
                  onTableRowClick={(row) => {
                    if (prevRowRef.current === row.id) return;
                    prevRowRef.current = row.id;
                    setSelectedTableInfo({
                      id: row.id,
                      insIds: [
                        row.instrumentId1,
                        row.instrumentId2,
                        row.instrumentId3,
                        row.instrumentId4,
                      ],
                    });
                  }}
                  activeRowId={
                    selectedTableInfo ? selectedTableInfo?.id : undefined
                  }
                  tableBoxClassName="max-h-[500px]"
                  ref={prevRowRef}
                />
              )}
              refreshTableTime={30000}
              getDataTable={(
                searchParameters,
                offset,
                pageSize,
                sortingType,
                additional,
                axiosOptions,
              ) =>
                getPairPositionTable(
                  searchParameters,
                  offset,
                  pageSize,
                  sortingType,
                  additional,
                  axiosOptions,
                  apiTab,
                )
              }
              searchParameters={serverSearchParams}
              withSort
            />
          </div>
        </div>
      </div>
    </>
  );
};
export default ArtificialPairPosition;
